import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import { loadRecitals } from '../recitalRecord';
import { materializeRecital } from '../materializeRecital';
import { discoverPlaceholders, humanizeToken, computeMilestoneDates, discoverDateTokens } from '../recitalTokens';
function errorMessage(error, fallback) {
  var errors = error && error.response && error.response.errors ? error.response.errors : error && error.errors ? error.errors : null;
  if (errors && errors.length) {
    return errors[0].detail || errors[0].title || fallback;
  }
  return error && error.message || fallback;
}
export default class CreateRecitalModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.types = app.forum.attribute('recitalsRecitalTypes') || [];
    this.recitals = app.store && app.store.all ? app.store.all('recitals') : [];
    // No default selection — the coordinator must explicitly choose a type.
    this.recitalTypeId = this.attrs.recitalTypeId || '';
    this.recitalNumber = this.recitalTypeId ? this.suggestNextNumber(this.recitalTypeId) : '';
    this.numberEdited = false;
    this.loadingRecitals = true;
    this.saving = false;
    this.error = null;
    // A recital row already created by a prior submit attempt whose
    // materialize step failed; reused on retry so we resume instead of
    // creating a duplicate row (which would 422 on the unique type+number).
    this.createdRecital = null;
    this.placeholderValues = {};
    this.placeholders = this.discoverForType(this.recitalTypeId);
    this.prefillDateDefaults(this.recitalTypeId);
    loadRecitals().then(recitals => {
      this.recitals = recitals || [];
      if (!this.numberEdited && this.recitalTypeId) {
        this.recitalNumber = this.suggestNextNumber(this.recitalTypeId);
      }
      this.loadingRecitals = false;
      m.redraw();
    }).catch(() => {
      this.loadingRecitals = false;
      m.redraw();
    });
  }
  className() {
    return 'CreateRecitalModal Modal--small';
  }
  title() {
    return app.translator.trans('pianotell-recitals.forum.create_recital_title');
  }
  content() {
    if (!this.types.length) {
      return m('.Modal-body', [m('.Alert.Alert--error', app.translator.trans('pianotell-recitals.forum.create_recital_no_types'))]);
    }
    return m('.Modal-body', [m('form', {
      onsubmit: e => {
        e.preventDefault();
        this.submit();
      }
    }, [m('.Form-group', [m('label', app.translator.trans('pianotell-recitals.forum.create_recital_type')), m('select.FormControl.CreateRecitalModal-type', {
      value: this.recitalTypeId,
      onchange: e => {
        this.recitalTypeId = e.target.value;
        this.numberEdited = false;
        this.recitalNumber = this.recitalTypeId ? this.suggestNextNumber(this.recitalTypeId) : '';
        this.placeholders = this.discoverForType(this.recitalTypeId);
        this.placeholderValues = {};
        this.prefillDateDefaults(this.recitalTypeId);
      }
    }, [m('option', {
      value: '',
      key: '_placeholder',
      disabled: true,
      selected: !this.recitalTypeId
    }, app.translator.trans('pianotell-recitals.forum.create_recital_type_placeholder')), ...this.types.map(type => m('option', {
      value: type.id,
      key: type.id
    }, type.label || type.id))])]), m('.Form-group', [m('label', app.translator.trans('pianotell-recitals.forum.create_recital_number')), m('input.FormControl.CreateRecitalModal-number', {
      type: 'number',
      min: 1,
      value: this.recitalNumber,
      oninput: e => {
        this.numberEdited = true;
        this.recitalNumber = e.target.value;
      }
    }), m('.helpText', this.loadingRecitals ? app.translator.trans('pianotell-recitals.forum.create_recital_loading_numbers') : app.translator.trans('pianotell-recitals.forum.create_recital_number_help'))]), (this.placeholders || []).map(name => m('.Form-group', {
      key: name
    }, [m('label', humanizeToken(name)), m('input.FormControl.CreateRecitalModal-placeholder', {
      type: 'text',
      'data-placeholder': name,
      value: this.placeholderValues[name] || '',
      oninput: e => {
        this.placeholderValues[name] = e.target.value;
      }
    }), m('.helpText', app.translator.trans('pianotell-recitals.forum.create_recital_placeholder_help', {
      token: '{' + name + '}'
    }))])), this.error ? m('.Alert.Alert--error', this.error) : null, m('.Form-group', {
      style: 'text-align: right;'
    }, [Button.component({
      className: 'Button Button--primary CreateRecitalModal-submit',
      type: 'submit',
      loading: this.saving,
      disabled: this.saving || !this.recitalTypeId
    }, app.translator.trans('pianotell-recitals.forum.create_recital_submit'))])])]);
  }
  findType(typeId) {
    for (var i = 0; i < this.types.length; i++) {
      if (String(this.types[i].id) === String(typeId)) return this.types[i];
    }
    return null;
  }

  // Bare {placeholder} tokens (e.g. {piece}) plus the computed auto-date tokens
  // ({auto_submissions_open_date}/{auto_golive_date}) used across this type's
  // create-time + transition templates. Both are captured once at creation and
  // reused by later title transitions; the auto-date tokens are pre-filled with
  // offset-based defaults the coordinator can review/update.
  discoverForType(typeId) {
    var t = this.findType(typeId);
    if (!t) return [];
    var templates = [t.announcement_creation_title, t.announcement_meta_title, t.submission_creation_title, t.title_template, t.announcement_template, t.submission_opening_body, t.announcement_update_open, t.announcement_update_golive];
    return [...discoverPlaceholders(templates), ...discoverDateTokens(templates)];
  }

  // Pre-fill discovered auto-date tokens with offset-based defaults
  // (announcement date = today); the coordinator can review/update them.
  prefillDateDefaults(typeId) {
    var type = this.findType(typeId);
    var defaults = computeMilestoneDates(type, new Date());
    (this.placeholders || []).forEach(name => {
      if (defaults[name] != null && (this.placeholderValues[name] == null || this.placeholderValues[name] === '')) {
        this.placeholderValues[name] = defaults[name];
      }
    });
  }
  suggestNextNumber(typeId) {
    var max = 0;
    // The highest number per type (including soft-deleted published recitals)
    // is provided by the server-computed recitalsRecitalNumbers attribute.
    var published = app.forum.attribute('recitalsRecitalNumbers') || {};
    if (published && published[typeId] != null) {
      var publishedNumber = parseInt(published[typeId], 10);
      if (publishedNumber > max) max = publishedNumber;
    }

    // Also consider any in-progress recitals of this type.
    (this.recitals || []).forEach(function (recital) {
      if (!recital || !recital.recitalTypeId || !recital.recitalNumber) return;
      if (String(recital.recitalTypeId()) !== String(typeId)) return;
      var number = parseInt(recital.recitalNumber(), 10);
      if (number > max) max = number;
    });
    return max + 1;
  }
  submit() {
    if (this.saving) return;
    var recitalNumber = parseInt(this.recitalNumber, 10);
    if (!this.recitalTypeId || !recitalNumber || recitalNumber < 1) {
      this.error = app.translator.trans('pianotell-recitals.forum.create_recital_invalid');
      return;
    }
    this.saving = true;
    this.error = null;
    var attrs = {
      recitalTypeId: this.recitalTypeId,
      recitalNumber: recitalNumber
    };
    var cleaned = {};
    (this.placeholders || []).forEach(name => {
      var v = this.placeholderValues[name];
      if (v != null && String(v).trim() !== '') cleaned[name] = String(v);
    });
    if (Object.keys(cleaned).length) attrs.placeholderValues = cleaned;

    // Resume a partially-created recital (row saved, materialize failed) rather
    // than re-creating the row, which would 422 on the unique (type, number)
    // constraint. materializeRecital is idempotent and fills what's missing.
    var ensureRecital = this.createdRecital ? Promise.resolve(this.createdRecital) : app.store.createRecord('recitals').save(attrs).then(recital => {
      this.createdRecital = recital;
      return recital;
    });
    ensureRecital.then(recital => materializeRecital(recital)).then(recital => {
      this.saving = false;
      app.alerts.show({
        type: 'success'
      }, app.translator.trans('pianotell-recitals.forum.create_recital_success'));
      this.hide();
      // Take the coordinator to the (hidden) announcement thread to edit it natively.
      if (recital.announcementDiscussionId()) {
        m.route.set('/d/' + recital.announcementDiscussionId());
      }
    }).catch(error => {
      this.saving = false;
      this.error = errorMessage(error, app.translator.trans('pianotell-recitals.forum.create_recital_error'));
      app.alerts.show({
        type: 'error'
      }, this.error);
      m.redraw();
    });
  }
}
flarum.reg.add('pianotell-recitals', 'forum/components/CreateRecitalModal', CreateRecitalModal);